import { useEffect, useMemo } from 'react';
import { useFetch } from '../common/hooks/useFetch';
import { usePolling } from '../common/hooks/usePolling';
import { buildPublicScheduleUrl } from '../common/utils/api.utils';
import type { PublicScheduleAPIResponse } from '../types/api/publicSchedule.types';
import type { FetchState } from '../types/common/fetchState.types';
import { isFetchSuccess } from '../types/common/fetchState.types';

export interface UsePublicScheduleOptions {
  /**
   * Schedule key (e.g., 'academy-division-26')
   * Must be alphanumeric with hyphens and underscore only
   */
  key: string;

  /**
   * Whether the hook should fetch and poll
   * When false, the mount fetch and polling are suspended
   *
   * @default true
   */
  enabled?: boolean;
}

export interface UsePublicScheduleResult {
  /**
   * Current fetch state (discriminated union)
   * Check status field to access data, error, or loading state
   */
  state: FetchState<PublicScheduleAPIResponse>;

  /**
   * Manual refetch function
   * Useful for user-triggered refresh or error recovery
   */
  refetch: () => Promise<void>;

  /**
   * Convenience boolean flags
   */
  isLoading: boolean;
  isError: boolean;
  isSuccess: boolean;

  /**
   * Convenience data accessors
   * Returns null when data is not available
   */
  events: PublicScheduleAPIResponse['events'] | null;
  syncedAt: string | null;
}

/**
 * Hook for fetching public schedule data with auto-polling
 *
 * Composes useFetch and usePolling for complete functionality:
 * - Fetches schedule data from S3/CloudFront
 * - Validates URL parameters
 * - Auto-refreshes every 30 seconds (configurable)
 * - Provides convenience accessors for events and sync timestamp
 *
 * @example
 * ```typescript
 * const { state, events, isLoading, syncedAt } = usePublicSchedule({
 *   key: 'academy-division-26'
 * });
 *
 * // Check loading state
 * if (isLoading) {
 *   return <LoadingSpinner />;
 * }
 *
 * // Access data safely using type guards
 * if (isFetchSuccess(state)) {
 *   return <DataGrid rows={events} />;
 * }
 *
 * // Handle errors using type guards
 * if (isFetchError(state)) {
 *   return <ErrorMessage error={state.error.message} />;
 * }
 * ```
 */
export const usePublicSchedule = ({
  key,
  enabled = true,
}: UsePublicScheduleOptions): UsePublicScheduleResult => {
  const url = useMemo(() => buildPublicScheduleUrl(key), [key]);

  // Fetch hook (does not auto-fetch, we control it with useEffect)
  const { state, fetchData, isLoading, isError, isSuccess } =
    useFetch<PublicScheduleAPIResponse>({
      url,
    });

  // Initial fetch on mount when enabled and URL is valid.
  // Skip when a successful result for this url is already held, so
  // re-enabling (false -> true) does not trigger a redundant refetch.
  useEffect(() => {
    if (enabled && url && !isSuccess) {
      fetchData();
    }
  }, [enabled, url, isSuccess, fetchData]);

  // Polling hook for automatic updates
  usePolling({
    callback: fetchData,
    enabled,
    runOnMount: false, // Initial fetch handled by useEffect above
  });

  return {
    state,
    refetch: fetchData,
    isLoading,
    isError,
    isSuccess,
    // Convenience accessors - available when status is 'success' or when refreshing with cached data
    events: isFetchSuccess(state)
      ? state.data.events
      : (state.data?.events ?? null),
    syncedAt: isFetchSuccess(state)
      ? state.data.synced_at
      : (state.data?.synced_at ?? null),
  };
};
